/**
 * Best By delivery date hard block
 * Polls every Identixweb delivery date picker instance on the page (the app
 * can render on both the full cart page and the cart drawer). Blocks that
 * instance's checkout button and shows a warning when a cart item is too
 * close to its best-by date:
 *
 *  1. Item is ALREADY inside the block window today (best-by date is
 *     BLOCK_WINDOW_DAYS away or less). No delivery date can work, so
 *     checkout is blocked even before a date is picked and the customer
 *     is told to remove the item.
 *  2. Item is fine today, but the selected delivery date lands inside the
 *     block window. The customer can remove the item or pick an earlier date.
 *
 * Requires: each .cart-item element to carry a data-best-by="YYYY-MM-DD"
 * attribute (only present on products that have the Best By Date metafield
 * set) and ideally a data-product-title attribute for naming in the warning.
 *
 * This script does its own blocking and does NOT depend on the script in
 * sections/header.liquid. It never sets button.disabled (the Identixweb
 * script owns that, and two scripts setting it made the button flicker).
 * Instead it greys the button out with a CSS class and stops the click.
 */
(function () {
  if (window.__bestByWarningInit) return;
  window.__bestByWarningInit = true;

  var POLL_INTERVAL = 500;
  var BLOCK_WINDOW_DAYS = 5;
  var DATE_INPUT_SELECTOR = '.deliveryDateValue';
  var WIDGET_SELECTOR = '.shipping-identixweb-order-delivery-dates';
  var SCOPE_SELECTOR = '#main-cart-items, #CartDrawer';
  var DRAWER_BUTTON_SELECTOR = '#CartDrawer-Checkout, #CartDrawer .cart__checkout-button';
  var PAGE_BUTTON_SELECTOR = 'button[name="checkout"]:not(#CartDrawer-Checkout)';
  var WARNING_CLASS = 'best-by-warning-banner';
  var BLOCKED_CLASS = 'best-by-blocked';

  // Greyed-out look for a blocked button. !important so it wins over the
  // inline styles the Identixweb script puts on the button.
  var style = document.createElement('style');
  style.textContent =
    '.' + BLOCKED_CLASS + '{opacity:0.5 !important;cursor:not-allowed !important;pointer-events:none !important;}';
  document.head.appendChild(style);

  function parseDdMmYyyy(str) {
    if (!str) return null;
    var parts = str.split('/');
    if (parts.length !== 3) return null;
    var day = parseInt(parts[0], 10);
    var month = parseInt(parts[1], 10);
    var year = parseInt(parts[2], 10);
    if (!day || !month || !year) return null;
    return new Date(year, month - 1, day);
  }

  function parseIsoDate(str) {
    if (!str) return null;
    var parts = str.split('-');
    if (parts.length !== 3) return null;
    var year = parseInt(parts[0], 10);
    var month = parseInt(parts[1], 10);
    var day = parseInt(parts[2], 10);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  function startOfToday() {
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  function getBestByEntries(scope) {
    var items = scope.querySelectorAll('.cart-item[data-best-by]');
    var entries = [];
    items.forEach(function (item) {
      var raw = item.getAttribute('data-best-by');
      var date = parseIsoDate(raw);
      if (!date) return;
      // Block window starts BLOCK_WINDOW_DAYS before the actual best-by date
      date.setDate(date.getDate() - BLOCK_WINDOW_DAYS);
      var name = item.getAttribute('data-product-title') || 'An item in your cart';
      entries.push({ name: name, date: date });
    });
    return entries;
  }

  function joinNames(entries) {
    return entries.map(function (entry) { return entry.name; }).join(', ');
  }

  function ensureWarningEl(container) {
    var el = container.querySelector('.' + WARNING_CLASS);
    if (!el) {
      el = document.createElement('div');
      el.className = WARNING_CLASS;
      el.style.display = 'none';
      el.style.marginBottom = '12px';
      el.style.textAlign = 'left';
      el.style.padding = '12px 14px';
      el.style.border = '1px solid #b7786b';
      el.style.borderRadius = '8px';
      el.style.background = '#fbeee9';
      el.style.color = '#6b4a3f';
      el.style.fontSize = '13px';
      el.style.lineHeight = '1.5';
      container.insertBefore(el, container.firstChild);
    }
    return el;
  }

  // The checkout button(s) that belong to a cart container. On the cart page
  // the button sits outside #main-cart-items, so it is looked up on the page.
  function getButtons(scope) {
    if (scope.id === 'CartDrawer') return scope.querySelectorAll(DRAWER_BUTTON_SELECTOR);
    return document.querySelectorAll(PAGE_BUTTON_SELECTOR);
  }

  // The cart container a checkout button belongs to.
  function getScopeForButton(button) {
    return button.closest('#CartDrawer') || document.querySelector('#main-cart-items');
  }

  function isBlocked(scope) {
    return !!scope && scope.dataset.bestByBlocked === '1';
  }

  function setCheckoutBlocked(scope, blocked) {
    scope.dataset.bestByBlocked = blocked ? '1' : '';
    getButtons(scope).forEach(function (button) {
      button.classList.toggle(BLOCKED_CLASS, blocked);
    });
  }

  // Stop the checkout click / submit while blocked (covers keyboard too).
  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest('button[name="checkout"]');
    if (button && isBlocked(getScopeForButton(button))) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);

  document.addEventListener('submit', function (e) {
    var button = e.submitter;
    if (button && button.name === 'checkout' && isBlocked(getScopeForButton(button))) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);

  function checkAndWarn(dateInput) {
    var widget = dateInput.closest(WIDGET_SELECTOR);
    var scope = dateInput.closest(SCOPE_SELECTOR);
    if (!widget || !scope) return;

    // Show the warning right above the checkout button so it is always visible.
    var ctas = scope.querySelector('.cart__ctas');
    var warningEl = ensureWarningEl(ctas || widget);
    var selectedDate = parseDdMmYyyy(dateInput.value);
    var today = startOfToday();
    var entries = getBestByEntries(scope);

    // Already inside the block window today: no delivery date can work.
    var tooCloseNow = [];
    // Fine today, but inside the block window on the selected delivery date.
    var tooCloseForDate = [];

    entries.forEach(function (entry) {
      if (today >= entry.date) {
        tooCloseNow.push(entry);
      } else if (selectedDate && selectedDate >= entry.date) {
        tooCloseForDate.push(entry);
      }
    });

    if (tooCloseNow.length === 0 && tooCloseForDate.length === 0) {
      warningEl.style.display = 'none';
      warningEl.innerHTML = '';
      setCheckoutBlocked(scope, false);
      return;
    }

    var html = '';

    if (tooCloseNow.length > 0) {
      html +=
        '<strong>Please Note:</strong> ' + joinNames(tooCloseNow) +
        ' is within ' + BLOCK_WINDOW_DAYS + ' days of its best by date and cannot be delivered in time. ' +
        'Please remove the item(s) to proceed to checkout.';
    }

    if (tooCloseForDate.length > 0) {
      if (html) html += '<br><br>';
      html +=
        '<strong>Please Note:</strong> ' + joinNames(tooCloseForDate) +
        ' will be at or within ' + BLOCK_WINDOW_DAYS + ' days of its best by date on your selected delivery date. ' +
        'Please remove the item(s) or choose an earlier delivery date to proceed to checkout.';
    }

    warningEl.innerHTML = html;
    warningEl.style.display = 'block';
    setCheckoutBlocked(scope, true);
  }

  function poll() {
    // Clear the block and warning where the date picker is not present any more
    document.querySelectorAll(SCOPE_SELECTOR).forEach(function (scope) {
      if (!scope.querySelector(DATE_INPUT_SELECTOR)) {
        setCheckoutBlocked(scope, false);
        var stale = scope.querySelector('.' + WARNING_CLASS);
        if (stale) stale.style.display = 'none';
      }
    });

    var inputs = document.querySelectorAll(DATE_INPUT_SELECTOR);
    inputs.forEach(function (input) {
      checkAndWarn(input);
    });
    setTimeout(poll, POLL_INTERVAL);
  }

  poll();
})();